<template>
  <mf-loading-dialog :loading="loading">
    <base-page-layout title="Resultados do Banner">
      <ResultsHeader :campaign="campaign" :banner="true" :show-settings-button="true" offer-type="general" settingsButtonLabel="Ver configurações do banner" />
      <v-row>
        <v-col v-for="card in resultsCardsMetrics" :key="card.title" cols="3">
          <cbm-card
            simple-card
            :title="card.title"
            :number-text="card.numberText"
            :number-color="card.numberColor || ''"
            :percent-text="card.percentText"
            :percentage-tooltip="card.percentageTooltip"
            :percent-description="card.percentDescription"
            :tooltip="card.tooltip"
            :boolean-loader="loading"
            :is-currency="card.isCurrency"
            :unsetPercentageColors="card.unsetPercentageColors"
          >
            <template v-if="card.numberDescription" #custom-additional-value>
              <p class="mb-0 cashback-expiration">
                {{ card.numberDescription }}
              </p>
            </template>
          </cbm-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <funnel
            :campaign="campaign"
            :metrics="funnelMetrics"
            :headers="funnelHeaders"
            :baseMetric="funnelBaseMetric"
            :quickActionActive="false"
            :hideQuickActionFromFranchisee="true"
            :isMercapromo="false"
            :isHighlight="true"
            noDataText="Não há resultados neste banner"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <results-units-table :items="unitsMetrics" />
        </v-col>
      </v-row>
      <mf-action-buttons
        :cancel-button="{
          text: 'Fechar',
          action: backToBannersList,
          isVisible: true,
          isDisabled: false,
        }"
      />
    </base-page-layout>
  </mf-loading-dialog>
</template>

<script>
import BasePageLayout from '@/components/BasePageLayout.vue'
import CbmCard from '@/components/CbmCard.vue'
import ResultsHeader from '@/components/campaigns/ResultsHeader.vue'
import { QUERY_GET_COMMERCIAL_BY_ID } from '../../graphql/index'
import { apolloProvider } from '@/apollo-provider.js'
import { microservicesUrls } from '@/plugins/microservices/microservices'
import ResultsUnitsTable from '../components/results/UnitsTable.vue'

export default {
  name: 'Results',
  components: {
    BasePageLayout,
    ResultsHeader,
    CbmCard,
    Funnel: () => import('@/modules/campanhas/views/components/results/Funnel.vue'),
    ResultsUnitsTable,
  },
  data() {
    return {
      loading: false,
      campaign: {},
      resultsCardsMetrics: [],
      funnelMetrics: {},
      funnelBaseMetric: 'highlight',
      unitsMetrics: [],
      metricsEmpty: [],
    }
  },
  computed: {
    funnelHeaders() {
      return [
        { text: '', sortable: false },
        { text: 'Clientes', sortable: false, align: 'right' },
        {
          text: '% dos que compraram',
          value: 'received',
          sortable: false,
          align: 'right',
          tooltip: 'Considerando o total de clientes que visualizaram o banner, qual percentual desses fez compra.',
        },
        { text: 'Faturamento', sortable: false, align: 'right' },
        { text: 'Ticket médio', sortable: false, align: 'right' },
        { text: 'Cupons', sortable: false, align: 'right' },
      ]
    },
  },
  mounted() {
    this.loadCommercialMetrics()
  },
  methods: {
    loadCommercialMetrics() {
      this.loading = true

      const id = this.$route.params.id

      this.fetchCommercial({ id })
        .then(({ commercial }) => {
          this.campaign = this.mapCampaign(commercial)
          this.resultsCardsMetrics = this.mapResultsMetrics(commercial.metrics ?? this.getCardsEmptyValues())
          this.funnelMetrics = this.mapFunnelMetrics(commercial.metrics.resultsByProfile)
          this.unitsMetrics = this.mapUnitsMetrics(commercial.metrics.unitsMetrics)
        })
        .catch((error) => {
          console.error('Erro ao buscar métricas:', error)
        })
        .finally(() => {
          this.loading = false
        })
    },

    fetchCommercial(variables) {
      return apolloProvider.defaultClient
        .query({
          query: QUERY_GET_COMMERCIAL_BY_ID,
          variables,
          fetchPolicy: 'no-cache',
          context: {
            uri: microservicesUrls['fedGateway'],
          },
        })
        .then(({ data }) => {
          if (!data.commercial) {
            throw new Error('comercial não encontrado.')
          }
          return { commercial: data.commercial }
        })
        .catch((error) => {
          console.error('Erro na consulta getCommercial:', error)
          throw error
        })
    },
    backToBannersList() {
      this.$router.push({ name: 'banners-list' }).catch(() => {})
    },
    mapCampaign(commercial) {
      return {
        _id: commercial._id,
        name: commercial.title,
        description: commercial.description,
        status: commercial.status,
        start_date: commercial.initial_date || '',
        end_date: commercial.final_date || '',
        image_url: commercial.image_url || '',
        priority: commercial.priority || 0,
        action: commercial.action || {},
        flash: false,
        smart: false,
        activatable: commercial.type === 'EXPANDED',
        units: (commercial.units || []).map((unit) => ({
          name: unit.name || '',
          id_erp: unit.id_erp || '',
          cnpj: unit.cnpj || '',
          address: unit.address || '',
          contact: unit.contact || '',
          ecommerce_v2_active: unit.ecommerce_v2_active || false,
          tags:
            unit.tags?.map((tag) => ({
              name: tag.name || '',
              color: tag.color || '',
            })) || [],
        })),
        offer_sales: [],
      }
    },

    mapResultsMetrics(metrics) {
      const formatCurrency = (value) =>
        new Intl.NumberFormat('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value || 0)

      return [
        {
          title: 'Clientes que visualizaram',
          numberText: (Number(metrics.bigNumbers?.clientsViewedTotalUsers) || 0).toLocaleString('pt-BR'),
          percentText: metrics.bigNumbers?.clientsClickedTotalUsers ? metrics.bigNumbers.clientsClickedTotalUsers.toLocaleString('pt-BR') : '0',
          percentDescription: 'clientes que clicaram',
          unsetPercentageColors: true,
          tooltip: 'Usuários autenticados do aplicativo que visualizaram esse banner.',
        },
        {
          title: 'Visualizaram e compraram',
          numberText: (Number(metrics.bigNumbers?.clientsViewedAndPurchasedSomethingTotalUsers) || 0).toLocaleString('pt-BR'),
          tooltip: 'Quantidade de clientes que viram o banner e realizaram pelo menos uma compra no período de mensuração.',
        },
        {
          title: 'Ticket médio',
          numberText: formatCurrency(Number(metrics.bigNumbers?.clientsViewedAndPurchasedSomethingAverageTicket) || 0),
          percentText: `${
            metrics.bigNumbers?.clientsViewedAndPurchasedSomethingAverageTicketPercentage === 0
              ? '0,0%'
              : `${
                  metrics.bigNumbers?.clientsViewedAndPurchasedSomethingAverageTicketPercentage > 0 ? '+' : ''
                }${metrics.bigNumbers?.clientsViewedAndPurchasedSomethingAverageTicketPercentage.toFixed(1).replace('.', ',')}%`
          }`,
          percentDescription: 'do ticket médio das lojas',
          unsetPercentageColors: false,
          isCurrency: true,
          tooltip:
            'Ticket médio dos clientes que viram o banner e realizaram pelo menos uma compra no período de mensuração em comparação com o ticket médio dos consumidores do clube no mesmo período dessa campanha.',
        },
        {
          title: 'Faturamento',
          numberText: formatCurrency(Number(metrics.bigNumbers?.clientsViewedAndPurchasedSomethingRevenue) || 0),
          percentText: metrics.bigNumbers?.clientsViewedAndPurchasedSomethingRevenuePercentage
            ? `${metrics.bigNumbers.clientsViewedAndPurchasedSomethingRevenuePercentage.toFixed(1).replace('.', ',')}%`
            : '0,0%',
          percentDescription: 'do faturamento total',
          unsetPercentageColors: true,
          isCurrency: true,
          tooltip: 'Faturamento dos clientes que viram o banner e realizaram pelo menos uma compra no período',
        },
      ]
    },

    mapFunnelMetrics(profileMetrics) {
      const mapMetrics = (metrics) => ({
        count: metrics?.clients,
        representation: metrics?.clientsPercentage,
        revenue: metrics?.revenue,
        average_ticket: metrics?.averageTicket,
        tickets: metrics?.tickets,
      })

      return {
        clients_metrics: {
          viewed_and_bought: mapMetrics(profileMetrics.clientsViewedAndPurchasedSomethingMetrics),
          viewed_with_products: mapMetrics(profileMetrics.clientsViewedAndPurchasedProductsMetrics),
          viewed_with_only_products: mapMetrics(profileMetrics.clientsViewedAndPurchasedOnlyTheProductsMetrics),
        },
      }
    },

    mapUnitsMetrics(unitsMetrics = []) {
      return unitsMetrics.map((metric) => ({
        name: metric.name,
        revenue: metric.revenue,
        discount: metric.discount,
        clients: metric.clients,
        averageTicket: metric.averageTicket,
      }))
    },

    getCardsEmptyValues() {
      this.metricsEmpty = {
        bigNumbers: {
          clientsViewedTotalUsers: 0,
          clientsClickedTotalUsers: 0,
          clientsViewedAndPurchasedSomethingTotalUsers: 0,
          clientsViewedAndPurchasedSomethingAverageTicket: 0,
          clientsViewedAndPurchasedSomethingAverageTicketPercentage: 0,
          clientsViewedAndPurchasedSomethingRevenue: 0,
          clientsViewedAndPurchasedSomethingRevenuePercentage: 0,
        },
      }

      return this.metricsEmpty
    },
  },
}
</script>
